import { motion } from "framer-motion";
import { PhoneFilledIcon } from "@/ui/components/voice-icons";
import { useRemoteDisplay } from "./use-voice-calling";
import { useVoice } from "./voice-provider";

/**
 * Incoming call prompt. Rendered by the VoiceProvider whenever a call is
 * ringing, regardless of the current page. The Twilio SDK plays its
 * built-in ringtone while the call is unanswered.
 *
 * Shares the dark call-surface treatment with the dialer, in-call widget,
 * and mobile call drawer (intentional in both themes — standard convention
 * for call UIs). Full-width banner on mobile, corner card at sm+.
 */
export function IncomingCallCard() {
  const { remote, acceptIncoming, rejectIncoming } = useVoice();
  const { name, formattedPhoneNumber } = useRemoteDisplay(remote);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed inset-x-4 top-4 z-[60] rounded-2xl border border-white/10 bg-zinc-950/95 p-4 text-zinc-50 shadow-2xl backdrop-blur sm:inset-x-auto sm:right-4 sm:w-80"
      role="alertdialog"
      aria-label="Incoming call"
    >
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <span className="size-1.5 animate-pulse rounded-full bg-green-400" />
        Incoming call
      </div>
      <div className="mt-1.5 min-w-0">
        <p className="truncate text-base font-semibold text-zinc-50">
          {name ?? formattedPhoneNumber ?? "Unknown caller"}
        </p>
        {name && formattedPhoneNumber ? (
          <p className="truncate text-sm text-zinc-400">{formattedPhoneNumber}</p>
        ) : null}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={rejectIncoming}
          className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-red-600 text-sm font-medium text-white transition-colors hover:bg-red-500"
        >
          <PhoneFilledIcon className="size-4 rotate-[135deg]" />
          Decline
        </button>
        <button
          type="button"
          onClick={acceptIncoming}
          className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-green-500 text-sm font-medium text-white transition-colors hover:bg-green-400"
        >
          <PhoneFilledIcon className="size-4" />
          Accept
        </button>
      </div>
    </motion.div>
  );
}
